<script setup lang="ts">
// Deep registry sections of the details screen. Everything here needs the
// full /mnt document, so the parent mounts this only once that source lands;
// the quick cards above it stream in from the faster sources independently.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  Axle,
  InspectionEntry,
  MntResponse,
  Severity,
  StatusMessage,
  Tire,
} from '../api/checkVehicle';
import {
  dash,
  formatBoolean,
  formatCc,
  formatDate,
  formatKg,
  formatKm,
  formatKw,
  formatLitersFromCc,
  formatMm,
  formatMoney,
  formatUnit,
} from '../vehicle/format';
import {
  mntFieldTranslation,
  translateAxleName,
  translateCategory,
  translateCountryName,
  translateDriveType,
  translateFuelCombination,
  translateGenericValue,
  translateInspectionDecision,
  translateInspectionType,
  translateInsuranceContract,
  translateLocation,
  translateMntValue,
  translateOperation,
  translateRegistrationContext,
  translateStatusMessage,
} from '../vehicle/mntTranslations';
import {
  translateBodyType,
  translateColor,
  translateFuelType,
  translateTransmission,
} from '../vehicle/soov';
import { localizeUnitText } from '../vehicle/units';

interface DetailRow {
  label: string;
  value: string;
  tone?: 'good' | 'bad' | 'warn' | 'muted';
}

const props = defineProps<{ response: MntResponse }>();

const { t, locale } = useI18n();

const vehicle = computed(() => props.response.vehicle);
const identification = computed(() => vehicle.value.identification);
const summary = computed(() => vehicle.value.summary);
const tech = computed(() => vehicle.value.technical_details);
const registrationPlate = computed(
  () =>
    identification.value.registration_number ??
    props.response.regnr ??
    identification.value.requested_registration_number ??
    '-',
);
const pledge = computed(() => vehicle.value.restrictions.register_pledge);

function row(labelKey: string, value: string, tone?: DetailRow['tone']): DetailRow {
  const detail: DetailRow = { label: t(labelKey), value };
  if (tone) detail.tone = tone;
  return detail;
}

function rawRows(values: Record<string, string | null | undefined>): DetailRow[] {
  return Object.entries(values)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0)
    .map(([label, value]) => {
      const descriptor = mntFieldTranslation(label);
      return {
        label: descriptor.labelKey ? t(descriptor.labelKey) : label,
        value:
          localizeUnitText(
            translateMntValue(descriptor.kind, value, locale.value) ?? value,
            locale.value,
          ) ?? value,
      };
    });
}

function statusTone(message: StatusMessage): DetailRow['tone'] {
  if (message.severity === 'success') return 'good';
  if (message.severity === 'warning') return 'warn';
  if (message.severity === 'error') return 'bad';
  return 'muted';
}

function categoryTone(status: string): DetailRow['tone'] {
  if (status === 'ok') return 'good';
  if (status === 'minor_fault') return 'warn';
  if (status === 'major_fault') return 'bad';
  return 'muted';
}

function severityLabel(severity: Severity): string {
  return t(`details.severity.${severity}`);
}

const performanceRows = computed(() => [
  row(
    'details.labels.power',
    formatKw(summary.value.engine_power_kw ?? tech.value.engine.power_kw, locale.value),
  ),
  row(
    'details.labels.displacement',
    `${formatLitersFromCc(
      summary.value.engine_displacement_cc ?? tech.value.engine.displacement_cc,
      locale.value,
    )} / ${formatCc(
      summary.value.engine_displacement_cc ?? tech.value.engine.displacement_cc,
      locale.value,
    )}`,
  ),
  row(
    'details.labels.topSpeed',
    formatUnit(tech.value.engine.max_speed_kmh, 'kilometerPerHour', locale.value),
  ),
  row('details.labels.kerbWeight', formatKg(tech.value.masses.kerb_weight_kg, locale.value)),
  row(
    'details.labels.fuelCombinedNedc',
    dash(localizeUnitText(tech.value.fuel_consumption.combined_nedc.raw, locale.value)),
  ),
  row(
    'details.labels.fuelCombinedWltp',
    dash(localizeUnitText(tech.value.fuel_consumption.combined_wltp.raw, locale.value)),
  ),
  row(
    'details.labels.fuelCity',
    dash(localizeUnitText(tech.value.fuel_consumption.city.raw, locale.value)),
  ),
  row(
    'details.labels.fuelHighway',
    dash(localizeUnitText(tech.value.fuel_consumption.highway.raw, locale.value)),
  ),
  row('details.labels.co2Nedc', dash(localizeUnitText(tech.value.environment.co2_nedc.raw, locale.value))),
  row('details.labels.co2Wltp', dash(localizeUnitText(tech.value.environment.co2_wltp.raw, locale.value))),
]);

const registrationRows = computed(() => [
  row('details.labels.firstRegistration', formatDate(tech.value.registration.first_registration)),
  row('details.labels.estoniaRegistration', formatDate(tech.value.registration.estonia_registration)),
  row('details.labels.origin', dash(translateCountryName(tech.value.registration.country_of_origin, locale.value))),
  row(
    'details.labels.originRaw',
    dash(
      translateRegistrationContext(
        tech.value.registration.first_registration_context,
        tech.value.registration.origin_raw,
        locale.value,
      ),
    ),
  ),
]);

const classificationRows = computed(() => [
  row('details.labels.category', dash(translateCategory(tech.value.classification.category, locale.value))),
  row('details.labels.categoryCode', dash(tech.value.classification.category_code)),
  row('details.labels.bodyType', dash(translateBodyType(tech.value.classification.body_type, locale.value))),
  row('details.labels.color', dash(translateColor(tech.value.classification.body_color, locale.value))),
  row(
    'details.labels.multicolor',
    formatBoolean(tech.value.classification.multicolor, t('details.values.yes'), t('details.values.no')),
  ),
]);

const engineRows = computed(() => [
  row('details.labels.engineModel', dash(tech.value.engine.model)),
  row('details.labels.displacement', formatCc(tech.value.engine.displacement_cc, locale.value)),
  row('details.labels.power', formatKw(tech.value.engine.power_kw, locale.value)),
  row('details.labels.fuel', dash(translateFuelType(tech.value.engine.fuel, locale.value))),
  row(
    'details.labels.fuelCombination',
    dash(translateFuelCombination(tech.value.engine.fuel_combination, locale.value)),
  ),
  row('details.labels.transmission', dash(translateTransmission(tech.value.engine.transmission, locale.value))),
  row('details.labels.drive', dash(translateDriveType(tech.value.engine.drive_type, locale.value))),
  row(
    'details.labels.topSpeed',
    formatUnit(tech.value.engine.max_speed_kmh, 'kilometerPerHour', locale.value),
  ),
  row(
    'details.labels.speedLimit',
    formatUnit(tech.value.engine.speed_limit_kmh, 'kilometerPerHour', locale.value),
  ),
  row(
    'details.labels.catalyst',
    formatBoolean(tech.value.engine.catalyst, t('details.values.yes'), t('details.values.no')),
  ),
]);

const dimensionRows = computed(() => [
  row('details.labels.length', formatMm(tech.value.dimensions.length_mm, locale.value)),
  row('details.labels.width', formatMm(tech.value.dimensions.width_mm, locale.value)),
  row('details.labels.height', formatMm(tech.value.dimensions.height_mm, locale.value)),
  row('details.labels.track', formatMm(tech.value.dimensions.track_mm, locale.value)),
]);

const massRows = computed(() => [
  row('details.labels.grossWeight', formatKg(tech.value.masses.gross_weight_kg, locale.value)),
  row('details.labels.registeredMass', formatKg(tech.value.masses.registered_mass_kg, locale.value)),
  row('details.labels.kerbWeight', formatKg(tech.value.masses.kerb_weight_kg, locale.value)),
  row('details.labels.loadCapacity', formatKg(tech.value.masses.load_capacity_kg, locale.value)),
  row('details.labels.brakedTrailer', formatKg(tech.value.masses.braked_trailer_max_kg, locale.value)),
  row('details.labels.unbrakedTrailer', formatKg(tech.value.masses.unbraked_trailer_max_kg, locale.value)),
  row('details.labels.trainMass', formatKg(tech.value.masses.train_mass_kg, locale.value)),
  row('details.labels.couplingLoad', formatKg(tech.value.masses.coupling_load_kg, locale.value)),
]);

const environmentRows = computed(() => [
  row('details.labels.emissionStandard', dash(tech.value.environment.emission_standard)),
  row('details.labels.co2Nedc', dash(localizeUnitText(tech.value.environment.co2_nedc.raw, locale.value))),
  row('details.labels.co2Wltp', dash(localizeUnitText(tech.value.environment.co2_wltp.raw, locale.value))),
  row(
    'details.labels.standingNoise',
    dash(localizeUnitText(tech.value.environment.standing_noise.raw, locale.value)),
  ),
  row(
    'details.labels.drivingNoise',
    dash(localizeUnitText(tech.value.environment.driving_noise.raw, locale.value)),
  ),
]);

const axleSummaryRows = computed(() => [
  row('details.labels.axleCount', dash(tech.value.axles_summary.axle_count)),
  row('details.labels.wheelCount', dash(tech.value.axles_summary.wheel_count)),
  row('details.labels.wheelbase', formatMm(tech.value.axles_summary.wheelbase_mm, locale.value)),
  row('details.labels.bogieCount', dash(tech.value.axles_summary.bogie_count)),
]);

const otherRows = computed(() => [
  row('details.labels.vinLocation', dash(translateLocation(tech.value.other.vin_location, locale.value))),
  row(
    'details.labels.manufacturerPlateLocation',
    dash(translateLocation(tech.value.other.manufacturer_plate_location, locale.value)),
  ),
  row('details.labels.doors', dash(tech.value.other.doors)),
  row('details.labels.seats', dash(tech.value.other.seats)),
  row('details.labels.modification', dash(translateGenericValue(tech.value.other.modification, locale.value))),
  row('details.labels.type', dash(tech.value.other.type)),
  row('details.labels.variant', dash(tech.value.other.variant)),
  row('details.labels.version', dash(tech.value.other.version)),
  row('details.labels.typeApproval', dash(tech.value.other.type_approval_number)),
  row('details.labels.typeApprovalExtension', dash(tech.value.other.type_approval_extension)),
  row('details.labels.bodyCode', dash(tech.value.other.body_code)),
  row('details.labels.notes', dash(translateGenericValue(tech.value.other.notes, locale.value))),
  row('details.labels.conversionPermit', dash(translateGenericValue(tech.value.conversion_permit, locale.value))),
]);

const mileageRows = computed(() => [
  row('details.labels.latestMileage', formatKm(vehicle.value.mileage.summary.latest_km, locale.value)),
  row('details.labels.earliestMileage', formatKm(vehicle.value.mileage.summary.earliest_km, locale.value)),
  row('details.labels.totalKmGained', formatKm(vehicle.value.mileage.summary.total_km_gained, locale.value)),
  row(
    'details.labels.avgKmPerYear',
    formatUnit(vehicle.value.mileage.summary.avg_km_per_year, 'kilometerPerYear', locale.value),
  ),
  row('details.labels.rollbackCount', dash(vehicle.value.mileage.summary.rollback_count), vehicle.value.mileage.summary.has_rollback ? 'bad' : undefined),
  row('details.labels.firstReported', dash(vehicle.value.mileage.summary.first_reported_at?.date)),
  row('details.labels.lastReported', dash(vehicle.value.mileage.summary.last_reported_at?.date)),
]);

const insuranceRows = computed(() => [
  row(
    'details.labels.activeInsurance',
    formatBoolean(vehicle.value.insurance.has_active_contract, t('details.values.yes'), t('details.values.no')),
    vehicle.value.insurance.has_active_contract ? 'good' : 'bad',
  ),
]);

const rawSummaryRows = computed(() => rawRows(summary.value.raw_pairs));
const rawTechnicalRows = computed(() => rawRows(tech.value.raw_pairs));
const unmappedRows = computed(() => rawRows(props.response.raw.unmapped_tech_pairs));

function tireText(tire: Tire): string {
  if (tire.raw) return tire.raw;
  return [
    tire.width_mm,
    tire.aspect_ratio_pct ? `/${tire.aspect_ratio_pct}` : null,
    tire.construction,
    tire.rim_diameter_in,
    tire.load_index,
    tire.speed_symbol,
  ]
    .filter(Boolean)
    .join('');
}

function axleRows(axle: Axle): DetailRow[] {
  return [
    row('details.labels.maxAxleLoad', formatKg(axle.max_load_kg, locale.value)),
    row('details.labels.registeredAxleLoad', formatKg(axle.registered_load_kg, locale.value)),
    row(
      'details.labels.steeringAxle',
      formatBoolean(axle.steering_axle, t('details.values.yes'), t('details.values.no')),
    ),
    row(
      'details.labels.drivingAxle',
      formatBoolean(axle.driving_axle, t('details.values.yes'), t('details.values.no')),
    ),
  ];
}

function inspectionCategoryRows(entry: InspectionEntry): DetailRow[] {
  return Object.entries(entry.categories).map(([key, category]) =>
    row(
      `details.categories.${key}`,
      category.faults.length > 0
        ? `${t(`details.inspectionStatus.${category.status}`)} - ${category.faults.join('; ')}`
        : t(`details.inspectionStatus.${category.status}`),
      categoryTone(category.status),
    ),
  );
}
</script>

<template>
  <section class="detail-card lookup-meta-card">
    <div class="plate-badge">{{ registrationPlate }}</div>
    <p class="muted">{{ $t('details.fetchedAt') }} {{ new Date(response.fetched_at).toLocaleString() }}</p>
  </section>

  <!-- id="sec-*" anchors are scroll/pulse targets for the quick-card tap
       navigation in DetailsView (see useSectionPulse). -->
  <section v-if="vehicle.status_messages.length > 0" id="sec-status" class="status-list">
    <div
      v-for="message in vehicle.status_messages"
      :key="`${message.kind}-${message.text}`"
      class="status-pill"
      :class="statusTone(message)"
    >
      <strong>{{ severityLabel(message.severity) }}</strong>
      <span>{{ translateStatusMessage(message, locale) }}</span>
    </div>
  </section>

  <section id="sec-performance" class="detail-card wide-card">
    <h2>{{ $t('details.sections.performance') }}</h2>
    <div class="metric-grid">
      <div v-for="item in performanceRows" :key="item.label" class="metric-tile" :class="item.tone">
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
      </div>
    </div>
  </section>

  <section id="sec-operations" class="detail-card">
    <h2>{{ $t('details.sections.operations') }}</h2>
    <article
      v-for="entry in vehicle.operations_history.entries"
      :key="`${entry.date.raw}-${entry.operation_count}`"
      class="operation-row"
    >
      <time>{{ formatDate(entry.date) }}</time>
      <span>{{ entry.operations.map((operation) => translateOperation(operation.code, operation.raw, locale)).join('; ') }}</span>
    </article>
    <p v-if="vehicle.operations_history.entries.length === 0" class="muted">{{ $t('details.empty') }}</p>
  </section>

  <section class="detail-grid">
    <section id="sec-registration" class="detail-card">
      <h2>{{ $t('details.sections.registration') }}</h2>
      <dl class="details-list">
        <template v-for="item in registrationRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="detail-card">
      <h2>{{ $t('details.sections.classification') }}</h2>
      <dl class="details-list">
        <template v-for="item in classificationRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section id="sec-engine" class="detail-card">
      <h2>{{ $t('details.sections.engine') }}</h2>
      <dl class="details-list">
        <template v-for="item in engineRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="detail-card">
      <h2>{{ $t('details.sections.dimensions') }}</h2>
      <dl class="details-list">
        <template v-for="item in dimensionRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section id="sec-masses" class="detail-card">
      <h2>{{ $t('details.sections.masses') }}</h2>
      <dl class="details-list">
        <template v-for="item in massRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="detail-card">
      <h2>{{ $t('details.sections.environment') }}</h2>
      <dl class="details-list">
        <template v-for="item in environmentRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="detail-card">
      <h2>{{ $t('details.sections.axlesSummary') }}</h2>
      <dl class="details-list">
        <template v-for="item in axleSummaryRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="detail-card">
      <h2>{{ $t('details.sections.other') }}</h2>
      <dl class="details-list">
        <template v-for="item in otherRows" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
      </dl>
    </section>
  </section>

  <section id="sec-restrictions" class="detail-card">
    <h2>{{ $t('details.sections.restrictions') }}</h2>
    <p v-if="!pledge.has_entries" class="good">{{ $t('details.values.noPledge') }}</p>
    <dl v-else class="details-list">
      <template v-for="entry in pledge.entries" :key="`${entry.set_date.raw}-${entry.pledgee.raw}`">
        <dt>{{ formatDate(entry.set_date) }}</dt>
        <dd class="bad">
          {{ dash(entry.pledgee.name ?? entry.pledgee.raw) }} · {{ formatMoney(entry.amount, locale) }}
        </dd>
      </template>
    </dl>
  </section>

  <section class="detail-card">
    <h2>{{ $t('details.sections.insurance') }}</h2>
    <dl class="details-list">
      <template v-for="item in insuranceRows" :key="item.label">
        <dt>{{ item.label }}</dt>
        <dd :class="item.tone">{{ item.value }}</dd>
      </template>
      <template v-for="entry in vehicle.insurance.entries" :key="entry.document_number ?? entry.valid_until.raw">
        <dt>{{ entry.insurer ?? $t('details.labels.insurer') }}</dt>
        <dd>
          {{ dash(translateInsuranceContract(entry.contract_type, locale)) }} · {{ formatDate(entry.valid_from) }} -
          {{ formatDate(entry.valid_until) }}
        </dd>
      </template>
    </dl>
  </section>

  <section id="sec-mileage" class="detail-card wide-card">
    <h2>{{ $t('details.sections.mileage') }}</h2>
    <dl class="details-list compact-list">
      <template v-for="item in mileageRows" :key="item.label">
        <dt>{{ item.label }}</dt>
        <dd :class="item.tone">{{ item.value }}</dd>
      </template>
    </dl>
    <div v-if="vehicle.mileage.points.length > 0" class="timeline-list">
      <div v-for="point in vehicle.mileage.points" :key="point.index" class="timeline-row" :class="{ bad: point.is_rollback }">
        <span>{{ point.reported_at.date ?? point.reported_at.iso }}</span>
        <strong>{{ formatKm(point.odometer_km, locale) }}</strong>
        <small v-if="point.country_code">{{ point.country_code }}</small>
      </div>
    </div>
  </section>

  <section id="sec-inspections" class="detail-section">
    <h2>{{ $t('details.sections.inspections') }}</h2>
    <article
      v-for="entry in vehicle.inspections.entries"
      :key="`${entry.inspection_date.raw}-${entry.station}`"
      class="detail-card repeated-card"
    >
      <div class="inspection-head">
        <strong>{{ formatDate(entry.inspection_date) }}</strong>
        <span :class="categoryTone(entry.decision.status)">
          {{ translateInspectionDecision(entry.decision.status, locale) }}
        </span>
      </div>
      <dl class="details-list">
        <dt>{{ $t('details.labels.validUntil') }}</dt>
        <dd>{{ formatDate(entry.valid_until) }}</dd>
        <dt>{{ $t('details.labels.station') }}</dt>
        <dd>{{ dash(entry.station) }}</dd>
        <dt>{{ $t('details.labels.inspectionType') }}</dt>
        <dd>{{ dash(translateInspectionType(entry.type.code, entry.type.raw, locale)) }}</dd>
        <template v-for="item in inspectionCategoryRows(entry)" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd :class="item.tone">{{ item.value }}</dd>
        </template>
      </dl>
    </article>
    <p v-if="vehicle.inspections.entries.length === 0" class="muted">{{ $t('details.empty') }}</p>
  </section>

  <section class="detail-card wide-card">
    <h2>{{ $t('details.sections.tires') }}</h2>
    <div class="pill-list">
      <span v-for="tire in tech.tires.allowed_specs" :key="tire.raw ?? tireText(tire)" class="data-pill">
        {{ tireText(tire) }}
      </span>
      <span v-if="tech.tires.allowed_specs.length === 0" class="muted">{{ $t('details.empty') }}</span>
    </div>
  </section>

  <section class="detail-section">
    <h2>{{ $t('details.sections.axles') }}</h2>
    <article
      v-for="(axle, axleIndex) in tech.axles"
      :key="axle.axle_name ?? axle.position_index ?? axleIndex"
      class="detail-card repeated-card"
    >
      <h3>{{ translateAxleName(axle.axle_name, locale) ?? `${$t('details.labels.axle')} ${axle.position_index ?? ''}` }}</h3>
      <dl class="details-list">
        <template v-for="item in axleRows(axle)" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </template>
        <dt>{{ $t('details.labels.tires') }}</dt>
        <dd>{{ axle.tires.map(tireText).join(', ') || '-' }}</dd>
      </dl>
    </article>
  </section>

  <section v-if="rawSummaryRows.length > 0" class="detail-card">
    <dl class="details-list">
      <template v-for="item in rawSummaryRows" :key="item.label">
        <dt>{{ item.label }}</dt>
        <dd>{{ item.value }}</dd>
      </template>
    </dl>
  </section>

  <section v-if="rawTechnicalRows.length > 0" class="detail-card">
    <dl class="details-list">
      <template v-for="item in rawTechnicalRows" :key="item.label">
        <dt>{{ item.label }}</dt>
        <dd>{{ item.value }}</dd>
      </template>
    </dl>
  </section>

  <section v-if="unmappedRows.length > 0" class="detail-card">
    <dl class="details-list">
      <template v-for="item in unmappedRows" :key="item.label">
        <dt>{{ item.label }}</dt>
        <dd>{{ item.value }}</dd>
      </template>
    </dl>
  </section>
</template>
